import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useEffectOnce } from 'react-use';
import { theme } from '@xbto/design-system';
import { API } from '@xbto/api-client';
import {
  ActivityList,
  Col,
  SectionTitle,
  Pagination,
  useBreakpoints,
  Row,
  Button,
  ErrorCallout,
} from '@xbto/universal-components';
import { DataStore } from '~/store';
import { useDownloadActivitiesCsv } from '~/components/transactions/use-download-csv';

type Props = {
  currencyCode: string;
};

export function SectionActivity({ currencyCode }: Props) {
  // Params
  const { id: accountId } = useParams();

  // Store
  const error = DataStore.useStoreState(
    s => s.transactions.paginatedActivitiesError
  );
  const paginator = DataStore.useStoreState(
    s => s.transactions.paginatedActivities
  );
  const filters = DataStore.useStoreState(
    s => s.transactions.paginatedActivitiesFilters
  );

  const { getPaginatedActivities } = DataStore.useStoreActions(a => ({
    getPaginatedActivities: a.transactions.getPaginatedActivities,
  }));

  // Vars
  const {
    breakpoints: { XS },
  } = useBreakpoints();

  const downloadCsv = useDownloadActivitiesCsv();

  // Methods
  const refresh = useCallback(
    (req?: Partial<API.GetActivityTransactionsRequest>) => {
      if (accountId) {
        getPaginatedActivities({
          request: {
            ...filters,
            ...req,
            pageSize: 5, // Force page size
            currencyCodeFilter: [currencyCode],
          },
          accountId,
          clearError: true,
        });
      }
    },
    [accountId, currencyCode, filters, getPaginatedActivities]
  );

  // Handlers
  const handlePressPage = useCallback(
    (newPage: number) => {
      refresh({ page: newPage });
    },
    [refresh]
  );

  const handleSort = useCallback(
    (_id: string, dir: API.SortDirection) => {
      refresh({ sortColumn: API.SortColumn.Date, sortDirection: dir });
    },
    [refresh]
  );

  const handlePressExport = useCallback(() => {
    if (accountId) {
      downloadCsv(accountId);
    }
  }, [accountId, downloadCsv]);

  // Effects
  useEffectOnce(() => {
    refresh({ sortDirection: API.SortDirection.Descending });
  });

  // Render
  const TitleContainer = XS ? Col : Row.LR;

  return !paginator?.result.length && paginator?.page === 1 ? null : (
    <>
      <TitleContainer mb="6" style={{ rowGap: theme.spacing['4'] }}>
        <SectionTitle>Activity</SectionTitle>
        <Button
          label="Export CSV"
          variant="tertiary"
          size="sm"
          onPress={handlePressExport}
        />
      </TitleContainer>

      {!!error && (
        <ErrorCallout
          title="Unable to display activity"
          message="We are unable to display your activity at the moment, please try again later"
          height={336}
        />
      )}

      {!error && !!paginator && (
        <>
          <ActivityList
            data={paginator.result}
            onSort={handleSort}
            sortId="creationDate"
            sortDir={filters.sortDirection ?? API.SortDirection.Descending}
          />
          {paginator.totalCount > paginator.pageSize && (
            <Col mt="6">
              <Pagination
                onPressPage={handlePressPage}
                page={paginator.page}
                pageSize={paginator.pageSize}
                totalCount={paginator.totalCount}
                totalPages={paginator.totalPages}
              />
            </Col>
          )}
        </>
      )}
    </>
  );
}
